import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function PaymentsPage() {
    const [payments, setPayments] = useState([]);
    const [clients, setClients] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        client_id: '',
        client_service_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        due_date: '',
        payment_method: 'bank_transfer',
        status: 'pending',
        invoice_number: '',
        description: '',
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [paymentsRes, clientsRes, servicesRes] = await Promise.all([
                api.getPayments(),
                api.getClients(),
                api.getServices()
            ]);
            setPayments(paymentsRes.data.payments || []);
            setClients(clientsRes.data.clients || []);
            setServices(servicesRes.data.services || []);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.createPayment(formData);
            setShowModal(false);
            resetForm();
            loadData();
        } catch (error) {
            alert('Error creating payment');
        }
    };

    const resetForm = () => {
        setFormData({
            client_id: '',
            client_service_id: '',
            amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            due_date: '',
            payment_method: 'bank_transfer',
            status: 'pending',
            invoice_number: '',
            description: '',
            notes: ''
        });
    };

    const updatePaymentStatus = async (id, status) => {
        try {
            await api.updatePayment(id, { status });
            loadData();
        } catch (error) {
            alert('Error updating payment');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount || 0);
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="page">
            <div className="page-header">
                <h1>Payments</h1>
                <button onClick={() => setShowModal(true)} className="btn-primary">
                    + Add Payment
                </button>
            </div>

            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>Invoice #</th>
                            <th>Client</th>
                            <th>Amount</th>
                            <th>Payment Date</th>
                            <th>Method</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map(payment => (
                            <tr key={payment.id}>
                                <td>{payment.invoice_number || `INV-${payment.id}`}</td>
                                <td>
                                    <strong>{payment.client_name}</strong>
                                    {payment.service_name && <small> - {payment.service_name}</small>}
                                </td>
                                <td className="amount">{formatCurrency(payment.amount)}</td>
                                <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                                <td>{payment.payment_method.replace('_', ' ')}</td>
                                <td>
                                    <select
                                        value={payment.status}
                                        onChange={(e) => updatePaymentStatus(payment.id, e.target.value)}
                                        className={`status-select ${payment.status}`}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                        <option value="overdue">Overdue</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </td>
                                <td>
                                    <button className="btn-sm">View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Add Payment</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Client *</label>
                                    <select
                                        value={formData.client_id}
                                        onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                                        required
                                    >
                                        <option value="">Select Client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.client_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Amount *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Payment Date *</label>
                                    <input
                                        type="date"
                                        value={formData.payment_date}
                                        onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Due Date</label>
                                    <input
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Payment Method</label>
                                    <select
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                                    >
                                        <option value="bank_transfer">Bank Transfer</option>
                                        <option value="credit_card">Credit Card</option>
                                        <option value="paypal">PayPal</option>
                                        <option value="cash">Cash</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                        <option value="overdue">Overdue</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Invoice Number</label>
                                <input
                                    type="text"
                                    value={formData.invoice_number}
                                    onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="2"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    Create Payment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PaymentsPage;
